import { Box, SxProps, Theme, Typography } from '@mui/material';

import { Supplier } from 'entities/Property/model/types/PropertySchema';
import { Seller } from 'entities/Proposal/model/types/ProposalSchema';
import { Vendor, VendorDisplay } from 'entities/Vendors';

import {
    isLightColor,
    isWhiteColor,
} from '../../../utils/colorUtils/colorUtils';

const NO_LOGO_VALUE = 'None';
const DEFAULT_BRAND_COLOR = 'FFF';

function hasImageUrl(obj: any): obj is Supplier {
    return obj && 'imageUrl' in obj;
}

function hasBrandColor(obj: any): obj is Supplier {
    return obj && 'brandColor' in obj;
}

interface SupplierLogoProps {
    size?: number;
    supplier?: Supplier | Seller;
    vendor?: Vendor | VendorDisplay;
    logo?: string;
    brandColor?: string;
    title?: string;
    sx?: SxProps<Theme>;
}

/**
 * A component for generating a graphical representation of a seller's logo.
 *
 * @example
 * // Basic usage with a seller object
 * <SupplierLogo supplier={seller} size={24} />
 *
 * @example
 * // Use without a seller object
 * <SupplierLogo
 *   logo="https://example.com/logo.png"
 *   brandColor="0000FF"
 *   title="Seller Co."
 *   size={24}
 * />
 */
export const SupplierLogo = ({
    size = 14,
    supplier,
    vendor,
    logo: propLogo,
    brandColor: propBrandColor,
    title: propTitle,
    sx,
}: SupplierLogoProps) => {
    let logo =
        propLogo ||
        vendor?.logo ||
        (hasImageUrl(supplier) ? supplier.imageUrl : undefined);

    if (logo === NO_LOGO_VALUE) {
        logo = null;
    }

    const brandColor =
        propBrandColor ||
        vendor?.brandColor ||
        (hasBrandColor(supplier) ? supplier.brandColor : undefined) ||
        DEFAULT_BRAND_COLOR;

    const title = propTitle || supplier?.name || vendor?.title || '';

    const isLightBrandColor = isLightColor(brandColor);
    const isWhiteBrandColor = isWhiteColor(brandColor);
    const imageSize = (8 * size) / 14;
    const textSize = (9 * size) / 14;

    return (
        <Box
            role="img"
            aria-label={`${title} logo`}
            sx={{
                width: `${size}px`,
                height: `${size}px`,
                boxSizing: 'border-box',
                backgroundColor: `#${brandColor}`,
                borderRadius: '50%',
                border: '1px solid',
                borderColor: isWhiteBrandColor ? '#D9D9D9' : '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...sx,
            }}
        >
            {logo ? (
                <img
                    src={logo}
                    alt={title}
                    style={{
                        maxWidth: `${imageSize}px`,
                        maxHeight: `${imageSize}px`,
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                    }}
                />
            ) : (
                <Typography
                    sx={{
                        fontSize: `${textSize}px`,
                        fontWeight: 800,
                        lineHeight: `${size}px`,
                        textAlign: 'center',
                        color: isLightBrandColor ? '#535353' : '#FFFFFF',
                    }}
                >
                    {title.slice(0, 1).toUpperCase()}
                </Typography>
            )}
        </Box>
    );
};
